import Autocomplete from "@mui/material/Autocomplete";
import type { TextFieldProps } from "@mui/material/TextField";
import TextField from "@mui/material/TextField";
import React, { useCallback } from "react";
import type {
  Control,
  FieldValues,
  Path,
  PathValue,
  UseControllerProps,
} from "react-hook-form";
import { useController } from "react-hook-form";
import { AutocompleteOption } from "~components/shared/Form/AutocompleteOption.tsx";

interface ItemSelectorSharedProps<T> {
  name?: string;
  options: readonly T[];
  labels: {
    label?: string;
    noOptions: string;
    error?: string;
  };
  isOptionEqualToValue: (option: T, value: T) => boolean;
  getOptionLabel: (option: T) => string;
  getOptionId: (option: T) => string;
  getOptionIcon?: (option: T) => React.ReactNode;
  filterSelectedOptions?: boolean;
  filterOptions?: Parameters<typeof Autocomplete<T>>[0]["filterOptions"];
  readOnly?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
  textFieldProps?: TextFieldProps;
}

interface ItemSelectorProps<T> extends ItemSelectorSharedProps<T> {
  onChange: (items: T) => void;
  onBlur?: (...event: unknown[]) => void;
  value: T;
  error?: boolean;
  hasFocus?: boolean;
}
function UnmemoizedItemSelector<T>({
  value,
  onChange,
  onBlur,
  name,
  options,
  labels,
  error = false,
  isOptionEqualToValue,
  getOptionLabel,
  getOptionId,
  getOptionIcon,
  filterSelectedOptions = true,
  readOnly,
  disabled,
  "data-testid": testId,
  textFieldProps,
  hasFocus,
  filterOptions,
}: ItemSelectorProps<T>) {
  return (
    <div data-testid={testId}>
      <Autocomplete
        autoComplete
        disabled={disabled}
        readOnly={readOnly}
        clearOnBlur
        openOnFocus
        options={options}
        filterOptions={filterOptions}
        value={value || null}
        isOptionEqualToValue={isOptionEqualToValue}
        getOptionLabel={getOptionLabel}
        filterSelectedOptions={filterSelectedOptions}
        clearIcon={null}
        blurOnSelect={true}
        noOptionsText={labels.noOptions}
        renderInput={(params) => (
          <TextField
            {...params}
            {...textFieldProps}
            name={name}
            autoFocus={hasFocus}
            label={labels.label}
            error={error}
            helperText={error ? labels.error : undefined}
          />
        )}
        renderOption={(props, option: T) => (
          <AutocompleteOption
            {...props}
            key={getOptionId(option)}
            icon={getOptionIcon?.(option)}
            label={getOptionLabel(option)}
            data-testid={`unselected-${getOptionId(option)}`}
          />
        )}
        onChange={(_e, items, reason) => {
          if (reason === "selectOption" && items) {
            onChange(items);
          }
        }}
        onBlur={onBlur}
      />
    </div>
  );
}

const MemoizedItemSelector = React.memo(
  UnmemoizedItemSelector,
) as typeof UnmemoizedItemSelector;

export { MemoizedItemSelector as ItemSelector };

type ArrayItemOrValue<V> = V extends Array<infer Item> ? Item : V;

interface ControlledItemSelectorProps<
  F extends FieldValues,
  P extends Path<F>,
  I extends ArrayItemOrValue<PathValue<F, P>>,
> extends ItemSelectorSharedProps<I> {
  control: Control<F>;
  name: P;
  rules?: UseControllerProps<F, P>["rules"];
  hasFocus?: boolean;
  beforeOnChange?: (value: I, proceed: () => void) => void;
}

export function ControlledItemSelector<
  F extends FieldValues = FieldValues,
  P extends Path<F> = Path<F>,
  I extends ArrayItemOrValue<PathValue<F, P>> = ArrayItemOrValue<
    PathValue<F, P>
  >,
>({
  control,
  name,
  rules,
  disabled,
  hasFocus,
  beforeOnChange,
  ...props
}: ControlledItemSelectorProps<F, P, I>) {
  // There seams to be a bug in react hook form when disabled is false
  // instead of undefined. When disabled is false fields are marked as dirty
  // although the fields are not changed.
  const disabledFixed = disabled || undefined;

  const {
    field: { onChange, onBlur, value: fieldValue },
    fieldState: { error },
  } = useController({ control, name, rules, disabled: disabledFixed });

  const onCustomChange = useCallback(
    (value: I) => {
      if (beforeOnChange) {
        beforeOnChange(value, () => onChange(value));
      } else {
        onChange(value);
      }
    },
    [beforeOnChange, onChange],
  );

  return (
    <MemoizedItemSelector<I>
      onChange={onCustomChange}
      hasFocus={hasFocus}
      onBlur={onBlur}
      value={fieldValue as I}
      name={name}
      error={!!error}
      disabled={disabledFixed}
      {...props}
    />
  );
}
